import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowAltCircleRight as faArrowAltCircleRightreg,
  faBell as faBellreg,
  faCheckCircle as faCheckCirclereg,
  faClock as faClockreg,
  faComment as faCommentreg,
  faEnvelope as faEnvelopereg,
  faMinusSquare as faMinusSquarereg,
  faTimesCircle as faTimesCirclereg,
} from '@fortawesome/free-regular-svg-icons';
import {
  faArchive,
  faArrowDown,
  faArrowsAlt,
  faArrowUp,
  faBan,
  faBars,
  faBell,
  faCalendarAlt,
  faCaretDown,
  faCaretRight,
  faCaretUp,
  faChalkboardTeacher,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCircle,
  faCirclePlay,
  faClipboardList,
  faClone,
  faCog,
  faCogs,
  faComment,
  faDatabase,
  faDirections,
  faDownLeftAndUpRightToCenter,
  faDownload,
  faEllipsisH,
  faEllipsisV,
  faEnvelope,
  faExternalLinkAlt,
  faEye,
  faEyeSlash,
  faFileAlt,
  faFileCsv,
  faFolder,
  faHome,
  faInbox,
  faKey,
  faList,
  faLock,
  faMapMarkerAlt,
  faMobileAlt,
  faPenNib,
  faPlay,
  faPlus,
  faPlusCircle,
  faPowerOff,
  faPrint,
  faSave,
  faSearch,
  faShare,
  faShareAlt,
  faSquare,
  faStamp,
  faSync,
  faTimes,
  faTimesCircle,
  faToolbox,
  faTrash,
  faTrashAlt,
  faUpload,
  faUpRightAndDownLeftFromCenter,
  faUser,
  faUserCheck,
  faUserCircle,
  faUserFriends,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { NgbAccordionConfig, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AdminModule } from '../admin/admin.module';
import { AuthInterceptor } from '../auth/auth.interceptor';
import { AuthModule } from '../auth/auth.module';
import { CoreModule } from '../core/core.module';
import { environment } from '../environments/environment';
import { ErrorsModule } from '../errors/errors.module';
import { GlobalErrorHandler } from '../errors/global-error-handler';
import { LoaderHttpInterceptor } from '../loader/loader.interceptor';
import { LoaderModule } from '../loader/loader.module';
import { LoaderService } from '../loader/loader.service';
import { PerimeterDto } from '../shared/models/perimeter.dto';
import { StoredPropertyChange } from '../shared/models/property-change';
import { ACCESS_MODE_CHANGE, PERIMETER_CHANGE } from '../shared/services/beneficiaries.service';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { SharedModule } from '../shared/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StartupServiceFactoryProvider } from './startup.service';

registerLocaleData(localeFr, 'fr');

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    FontAwesomeModule,
    AppRoutingModule,
    CoreModule,
    AuthModule,
    SharedModule,
    AdminModule,
    LoaderModule,
    ErrorsModule,
    BrowserAnimationsModule,
    NgbModule,
  ],
  providers: [
    StartupServiceFactoryProvider,
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderHttpInterceptor, multi: true, deps: [LoaderService] },
    {
      provide: PERIMETER_CHANGE,
      useFactory: (storage: LocalStorageService) => new StoredPropertyChange<PerimeterDto>(storage, environment.PERIMETER),
      deps: [LocalStorageService],
      multi: false,
    },
    {
      provide: ACCESS_MODE_CHANGE,
      useFactory: (storage: LocalStorageService) => new StoredPropertyChange<string>(storage, environment.ACCESS_MODE),
      deps: [LocalStorageService],
      multi: false,
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withXsrfConfiguration({
        cookieName: environment.XSRF_TOKEN_NAME,
      }),
    ),
  ],
})
export class AppModule {
  constructor(
    private readonly library: FaIconLibrary,
    private readonly ngbAccordionConfig: NgbAccordionConfig,
  ) {
    // Add an icon to the library for convenient access in other components
    this.library.addIcons(
      faArchive,
      faArrowAltCircleRightreg,
      faArrowsAlt,
      faArrowUp,
      faArrowDown,
      faBan,
      faBars,
      faBell,
      faBellreg,
      faCalendarAlt,
      faCaretDown,
      faCaretRight,
      faCaretUp,
      faChalkboardTeacher,
      faCheck,
      faCheckCirclereg,
      faChevronDown,
      faChevronLeft,
      faChevronRight,
      faChevronUp,
      faCircle,
      faCirclePlay,
      faClipboardList,
      faClockreg,
      faClone,
      faCog,
      faCogs,
      faComment,
      faCommentreg,
      faDatabase,
      faDirections,
      faDownLeftAndUpRightToCenter,
      faDownload,
      faEllipsisH,
      faEllipsisV,
      faEnvelope,
      faEnvelopereg,
      faExternalLinkAlt,
      faEye,
      faEyeSlash,
      faFileAlt,
      faFileCsv,
      faFolder,
      faFolder,
      faHome,
      faInbox,
      faKey,
      faList,
      faLock,
      faMapMarkerAlt,
      faMinusSquarereg,
      faMobileAlt,
      faPenNib,
      faPlay,
      faPlus,
      faPlusCircle,
      faPlusCircle,
      faPowerOff,
      faPrint,
      faSave,
      faSearch,
      faShare,
      faShareAlt,
      faSquare,
      faStamp,
      faSync,
      faTimes,
      faTimesCircle,
      faTimesCirclereg,
      faToolbox,
      faTrash,
      faTrashAlt,
      faUpload,
      faUpRightAndDownLeftFromCenter,
      faUser,
      faUserCheck,
      faUserCircle,
      faUserFriends,
      faUsers,
    );

    // If true (default) then data wrapped by the accordeon are destroyed so submit crashes
    this.ngbAccordionConfig.destroyOnHide = false;
  }
}
